import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from "rxjs/operators";
import { ExtraProductInformation } from "../data-models/extra-product-information.model";
import {
  isExtraProductInformationEnabled,
  ProductSource,
  WhiteLabelSetupConfiguration
} from "../data-models/wl-account-setup/wl-config-data.model";
import {
  ExtraProductInformationFactory,
  IOneShotWithExtraProductInformation
} from "../factories/extra-product-information-factory";
import { IntiaroPublicApiClient } from "./api/intiaro-public-api-client.service";
import { WhiteLabelConfigurationService } from "./white-labels/white-label-configuration.service";
import {
  ExtraProductInformationPresentation,
  presentExtraProductInformation
} from "../utils/extra-product-information-presenter";

/**
 * Extra product information lives in the product oneshot (metadata.product, elements, tagTypes and tags). The public
 * configurator API does not expose the oneshot, so it is read here separately.
 *
 * The block is optional: a white label without the feature asks for nothing, a product without the data
 * says nothing, and a failed request is worth a warning - the data may exist and simply did not arrive.
 */
@Injectable({
  providedIn: "root"
})
export class ExtraProductInformationService {

  private readonly factory: ExtraProductInformationFactory = new ExtraProductInformationFactory();
  private readonly reportedUnmatchedElementIds = new Set<string>();

  constructor(
    private intiaroPublicApiClient: IntiaroPublicApiClient,
    private whiteLabelConfigurationService: WhiteLabelConfigurationService
  ) {}

  /** Emits again when the admin switches the feature; the oneshot is fetched at most once per call and product source. */
  public watchForProduct(brandId: string, productId: string, context?: string): Observable<ExtraProductInformation> {
    if (!brandId || !productId) return of(new ExtraProductInformation());

    return this.watch(
      (productSource: ProductSource) => this.intiaroPublicApiClient.getProductOneShot(brandId, productId, productSource, context),
      `product ${productId}`
    );
  }

  public watchForCustomConfiguration(brandId: string, customConfigurationId: string, context?: string): Observable<ExtraProductInformation> {
    if (!brandId || !customConfigurationId) return of(new ExtraProductInformation());

    return this.watch(
      (productSource: ProductSource) =>
        this.intiaroPublicApiClient.getCustomConfigurationOneShot(brandId, customConfigurationId, productSource, context),
      `configuration ${customConfigurationId}`
    );
  }

  /** Rows for the current configuration. A configured element without data is skipped and reported once. */
  public present(information: ExtraProductInformation, configuration: unknown): ExtraProductInformationPresentation {
    const { presentation, unmatchedElementIds } = presentExtraProductInformation(information, configuration);
    const newlyUnmatched = unmatchedElementIds.filter((elementId) => !this.reportedUnmatchedElementIds.has(elementId));

    if (newlyUnmatched.length > 0) {
      newlyUnmatched.forEach((elementId) => this.reportedUnmatchedElementIds.add(elementId));
      // eslint-disable-next-line no-console
      console.warn(`Extra product information has no data for configured elements ${newlyUnmatched.join(", ")}. They are left out.`);
    }

    return presentation;
  }

  private watch(
    request: (productSource: ProductSource) => Observable<IOneShotWithExtraProductInformation>,
    resourceDescription: string
  ): Observable<ExtraProductInformation> {
    const informationBySource = new Map<ProductSource, Observable<ExtraProductInformation>>();
    const informationFrom = (productSource: ProductSource): Observable<ExtraProductInformation> => {
      const cachedInformation = informationBySource.get(productSource);
      if (cachedInformation) return cachedInformation;

      // catchError sits before map on purpose: it must swallow transport failures, never a mapping bug.
      const information = request(productSource).pipe(
        catchError((error: HttpErrorResponse) => {
          this.warnAboutFailure(error, resourceDescription);
          return of(null);
        }),
        map((oneShot: IOneShotWithExtraProductInformation | null) => this.factory.createFromOneShot(oneShot)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      informationBySource.set(productSource, information);

      return information;
    };

    return this.whiteLabelConfigurationService.whiteLabelSetupConfiguration.pipe(
      map((configuration: WhiteLabelSetupConfiguration) =>
        isExtraProductInformationEnabled(configuration) ? configuration.productSource : undefined),
      distinctUntilChanged(),
      switchMap((productSource?: ProductSource) => productSource ? informationFrom(productSource) : of(new ExtraProductInformation()))
    );
  }

  /** 404 means there is no oneshot to read, which the configurator reports on its own. Anything else is a failure. */
  private warnAboutFailure(error: HttpErrorResponse, resourceDescription: string): void {
    if (error?.status === 404) return;

    // eslint-disable-next-line no-console
    console.warn(
      `Extra product information for ${resourceDescription} could not be loaded (status ${error?.status ?? "unknown"}). The block stays hidden.`
    );
  }
}
